import { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMe, useLogout } from '../api/use-marketplace';
import { LanguageSwitcher } from './language-switcher';
import { CountrySwitcher } from './country-switcher';
import wordmarkUrl from '../assets/logo/wordmark.svg';

export function SiteShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <main className="flex-1">{children}</main>
      <SiteFooter />
    </div>
  );
}

export function SiteHeader() {
  const { t } = useTranslation();
  const me = useMe();
  const logout = useLogout();
  const user = me.data;

  return (
    <header className="sticky top-0 z-40 border-b border-border/40 bg-background/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" aria-label="Salunas home">
          <img src={wordmarkUrl} alt="Salunas" className="h-7 w-auto" />
        </Link>
        <nav className="flex items-center gap-1 sm:gap-3 text-sm">
          <NavLink
            to="/search"
            className={({ isActive }) =>
              [
                'hidden sm:inline px-3 py-1.5 rounded-full transition-colors',
                isActive ? 'text-foreground bg-secondary' : 'text-muted-foreground hover:text-foreground',
              ].join(' ')
            }
          >
            {t('common.browse')}
          </NavLink>
          {user ? (
            <>
              <NavLink
                to="/me/bookings"
                className={({ isActive }) =>
                  [
                    'px-3 py-1.5 rounded-full transition-colors',
                    isActive ? 'text-foreground bg-secondary' : 'text-muted-foreground hover:text-foreground',
                  ].join(' ')
                }
              >
                {t('common.myBookings')}
              </NavLink>
              <button
                type="button"
                onClick={() => logout.mutate()}
                className="px-3 py-1.5 rounded-full text-muted-foreground hover:text-foreground transition-colors"
              >
                {t('common.signOut')}
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="px-3 py-1.5 rounded-full text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('common.signIn')}
            </Link>
          )}
          <a
            href="https://clever-booking.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline px-3 py-1.5 rounded-full text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('common.forSalons')}
          </a>
          <CountrySwitcher />
          <LanguageSwitcher />
        </nav>
      </div>
    </header>
  );
}

export function SiteFooter() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-border/40 bg-background">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 py-12 flex flex-col md:flex-row justify-between items-start gap-8">
        <div>
          <img src={wordmarkUrl} alt="Salunas" className="h-6 w-auto mb-3" />
          <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
            {t('footer.tagline')}{' '}
            <a
              href="https://clever-booking.com"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 hover:text-foreground transition-colors"
            >
              {t('footer.builtBy')}
            </a>
            .
          </p>
        </div>
        <div className="flex gap-8 text-sm text-muted-foreground">
          <Link to="/search" className="hover:text-foreground transition-colors">
            {t('common.browse')}
          </Link>
          <Link to="/login" className="hover:text-foreground transition-colors">
            {t('common.signIn')}
          </Link>
          <a
            href="https://clever-booking.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground transition-colors"
          >
            {t('common.forSalons')}
          </a>
        </div>
      </div>
    </footer>
  );
}

export function formatAddress(raw: string | null | undefined): string {
  if (!raw) return '';
  try {
    const a = JSON.parse(raw) as { street?: string; city?: string; postalCode?: string };
    const parts = [a.street, [a.postalCode, a.city].filter(Boolean).join(' ').trim()].filter(Boolean);
    return parts.length ? parts.join(', ') : raw;
  } catch {
    return raw;
  }
}

export function categoryEmoji(key: string): string {
  switch (key) {
    case 'hair':    return '✂︎';
    case 'barber':  return '✦';
    case 'nails':   return '♢';
    case 'beauty':  return '✿';
    case 'massage': return '♨︎';
    case 'spa':     return '❀';
    default:        return '✦';
  }
}

export function subdomainHashHue(subdomain: string): number {
  return Array.from(subdomain).reduce(
    (acc, ch) => (acc * 31 + ch.charCodeAt(0)) % 360,
    0,
  );
}
